/**
 * Kiosk Settings Initial Values
 * This file contains all initial/default values extracted from each component
 */

import { v4 as uuidv4 } from "uuid";

import {
    GameLanguageCode,
    IApiAdvertisement,
    IApiBackground,
    IApiBidirectionalTranslation,
    IApiCharacterAvatar,
    IApiFestivalSetting,
    IApiFlipCardItem,
    IApiGamesToggle,
    IApiGreetingsWithToggle,
    IApiHintLanguage,
    IApiHintsObject,
    IApiInteraction,
    IApiInteractionData,
    IApiLanguageContent,
    IApiLayoutMode,
    IApiPhotoSettings,
    IApiPrizeItem,
    IApiPuzzleItem,
    IApiQRCode,
    IApiQuizItem,
    IApiSubtitleSpeed,
    IApiTimeAlerts,
    IApiVolume,
    IApiWeather,
    ICharacterAvatarFormData,
    IGameSettingsFormData,
    IKioskSettingNative,
    IScheduleMarqueeNew,
} from "../../types";
import {
    AUTO_START_COUNTDOWN_RANGE,
    GAME_LANGUAGES,
    GAME_TIME_RANGE,
    PAIR_COUNT_RANGE,
    PUZZLE_COUNT_RANGE,
    QUIZ_PASS_RANGE,
    QUIZ_TOTAL_RANGE,
} from "../constants";

// =============================================================================
// Time Alerts Settings (TimeAlertsSettings component)
// =============================================================================

/**
 * 預設時間提醒設定
 */
export const INITIAL_TIME_ALERTS_SETTINGS: IApiTimeAlerts = {
    enabled_time_alerts: false,
    data: [],
};

// =============================================================================
// Language Settings (LanguageSettings component)
// =============================================================================

/**
 * 預設語言設定
 * Default: zh-TW with 4 language options, dropdown display mode
 */
export const INITIAL_LANGUAGE_SETTINGS = {
    defaultLanguage: "zh-TW",
    languageOptions: ["zh-TW", "en-US", "ja-JP", "ko-KR"],
    languageDisplayMode: "dropdown" as const,
};

// =============================================================================
// Greeting Settings (GreetingSettingsModule component)
// =============================================================================

/**
 * 預設招呼詞
 * Default: Disabled with empty data array
 */
export const INITIAL_GREETINGS: IApiGreetingsWithToggle = {
    enabled: false,
    data: [],
};

// =============================================================================
// Hint Settings (HintSettingsModule component)
// =============================================================================

/**
 * 預設提示詞資料 (語言選項)
 * @language 語言
 * @options 提示詞(共三個)
 */
export const INITIAL_HINTS_DATA: IApiHintLanguage[] = [
    {
        language: "zh-TW",
        options: ["你好", "可以幫我介紹一下嗎?", "你可以做些什麼?"],
    },
    {
        language: "taiwanese",
        options: ["你好", "可以幫我介紹一下嗎?", "你可以做些什麼?"],
    },
    {
        language: "zh-CN",
        options: ["你好", "可以帮我介绍一下吗?", "你可以做些什么?"],
    },
    {
        language: "zh-HK",
        options: ["你好", "可以幫我介紹一下嗎?", "你可以做啲乜?"],
    },
    {
        language: "en-US",
        options: ["Hello", "Can you introduce it to me?", "What can you do?"],
    },
    {
        language: "en-GB",
        options: ["Hello", "Can you introduce it to me?", "What can you do?"],
    },
    {
        language: "ja-JP",
        options: ["こんにちは", "紹介してもらえますか?", "何ができるでしょうか?"],
    },
    {
        language: "ko-KR",
        options: ["안녕하세요", "소개해 주시겠습니까?", "무엇을 할 수 있습니까?"],
    },
    {
        language: "fr-FR",
        options: ["Bonjour", "Pouvez-vous me le présenter?", "Que pouvez-vous faire?"],
    },
    {
        language: "es-ES",
        options: ["Hola", "¿Puedes presentármelo?", "¿Qué puedes hacer?"],
    },
    {
        language: "ca-ES",
        options: ["Hola", "Me la pots presentar?", "Què pots fer?"],
    },
    {
        language: "vi-VN",
        options: ["Xin chào", "Bạn có thể giới thiệu nó cho tôi không?", "Bạn có thể làm gì?"],
    },
    {
        language: "th-TH",
        options: ["สวัสดี", "คุณช่วยแนะนำให้ฉันรู้จักได้ไหม?", "คุณทำอะไรได้บ้าง?"],
    },
    {
        language: "km-KH",
        options: ["សួស្តី", "តើអ្នកអាចណែនាំខ្ញុំបានទេ?", "តើអ្នកអាចធ្វើអ្វីបានខ្លះ?"],
    },
    {
        language: "ro-RO",
        options: ["Buna ziua", "Ai putea să mi-l prezinți?", "Ce poți face?"],
    },
    {
        language: "ru-RU",
        options: ["Привет", "Не могли бы вы мне его представить?", "Что вы можете сделать?"],
    },
];

/**
 * 預設提示詞 (新格式：物件形式)
 * @hints_icon 圖示類型 (finger | speaker)
 * @data 語言資料陣列
 */
export const INITIAL_HINTS: IApiHintsObject = {
    hints_icon: "finger",
    data: INITIAL_HINTS_DATA,
};

// =============================================================================
// Background Settings (BackgroundSettings component)
// =============================================================================

/**
 * 預設背景設定
 * Default: Empty URLs with centered position (0, 0)
 */
export const INITIAL_BACKGROUND_SETTINGS: IApiBackground = {
    v: {
        url: "",
        updated_time: "",
        position_x: 0,
        position_y: 0,
    },
    h: {
        url: "",
        updated_time: "",
        position_x: 0,
        position_y: 0,
    },
};

// =============================================================================
// Photo Filter Settings (PhotoFilterSettings component)
// =============================================================================

/**
 * 預設拍照設定
 * Default: Disabled photo with default filters enabled
 */
export const INITIAL_PHOTO_SETTINGS: IApiPhotoSettings = {
    enabled_photo: false,
    filter: {
        customized: false,
        filters_url: [],
        enable_default: true,
    },
    character: {
        enable: false,
    },
    line_id: "@159kdmqh",
    take_photo_sec: 3,
    close_result_sec: 10,
};

// =============================================================================
// QR Code Settings (QRCodeSettings component)
// =============================================================================

/**
 * 預設 QR Code 設定
 * Default: Disabled with URL input mode
 */
export const INITIAL_QRCODE_SETTINGS: IApiQRCode = {
    QRcode_show_mode: false,
    QRcode_Url: "",
    inputMode: "url",
};

// =============================================================================
// Volume Settings (VolumeSettings component)
// =============================================================================

/**
 * 預設音量設定
 * Default volumes:
 * - Voice: 100% (1.0)
 * - Effect: 60% (0.6)
 * - BGM max: 80% (0.8), min: 50% (0.5)
 * - Ad video max: 80% (0.8), min: 50% (0.5)
 */
export const INITIAL_VOLUME_SETTINGS: IApiVolume = {
    voice: 1,
    effect: 0.6,
    bgm_max: 0.8,
    bgm_min: 0.5,
    bgm_url: "",
    ad_video_max: 0.8,
    ad_video_min: 0.5,
};

// =============================================================================
// Festival Settings (FestivalSettings component)
// =============================================================================

/**
 * 預設節慶設定
 * Default: All festival features disabled
 */
export const INITIAL_FESTIVAL_SETTINGS: IApiFestivalSetting = {
    use_festival: false,
    setting: {
        bgm: false,
        bg: false,
        bg_obj: false,
        effect: false,
        character: false,
        character_is_conservative: false,
        filter: false,
        filter_keep_default: false,
    },
};

// =============================================================================
// Advertise Settings (AdvertiseSettings component)
// =============================================================================

/**
 * 預設廣告設定
 * Default: Disabled with empty media array, no shuffle
 */
export const INITIAL_AD_SETTINGS: IApiAdvertisement = {
    enabled: false,
    shuffle_mode: false,
    media: [],
};

// =============================================================================
// Marquee Settings (MarqueeSettings component)
// =============================================================================

/**
 * 預設跑馬燈設定
 * Default: Disabled with empty data array, position at top
 */
export const INITIAL_MARQUEE_SETTINGS: IScheduleMarqueeNew = {
    enable_marquee: false,
    position: "top",
    data: [],
};

// =============================================================================
// Weather Settings (WeatherSettings component)
// =============================================================================

/**
 * 預設天氣設定
 * Default: Disabled with no station selected, simple display mode
 */
export const INITIAL_WEATHER_SETTINGS: IApiWeather = {
    enabled_weather: false,
    display_description: false,
    station_name: "",
    station_code: "",
    country: "",
};

// =============================================================================
// Interaction Settings (Not implemented in components yet)
// =============================================================================

/**
 * 預設互動設定 (觸控 touch、辨識 gesture、自訂 custom)
 * 觸控與辨識模式的預設提示文字
 * Supports all available languages from get_kiosk_setting API
 */
export const INITIAL_TOUCH_HINTS: IApiInteractionData[] = [
    { language: "zh-TW", content: "點擊按鈕開始跟我互動吧！" },
    { language: "taiwanese", content: "點擊按鈕開始跟我互動吧！" },
    { language: "zh-CN", content: "点击按钮开始跟我互动吧！" },
    { language: "zh-HK", content: "點擊按鈕開始跟我互動吧！" },
    { language: "yue", content: "點擊按鈕開始同我互動吧！" },
    { language: "nan", content: "點擊按鈕開始跟我互動吧！" },
    { language: "en-US", content: "Click the button\nStart interacting with me" },
    { language: "en-GB", content: "Click the button\nStart interacting with me" },
    { language: "ja-JP", content: "ボタンをクリックして\n私との交流を始めましょう" },
    { language: "ko-KR", content: "버튼을 클릭하여\n나와 교류를 시작해 보세요" },
    { language: "fr-FR", content: "Cliquez sur le bouton\nCommencer à interagir avec moi" },
    { language: "es-ES", content: "Haga clic en el botón\nEmpieza a interactuar conmigo" },
    { language: "ca-ES", content: "Haga clic en el botón\nEmpieza a interactuar conmigo" },
    { language: "vi-VN", content: "Bấm vào nút\nBắt đầu tương tác với tôi" },
    { language: "th-TH", content: "คลิกปุ่ม\nเริ่มโต้ตอบกับฉัน" },
    { language: "km-KH", content: "ចុចប៊ូតុង\nចាប់ផ្តើមទំនាក់ទំនងជាមួយខ្ញុំ" },
    { language: "ro-RO", content: "Faceți clic pe butonul\nÎncepeți să interacționați cu mine" },
    { language: "ru-RU", content: "Нажмите кнопку\nНачать взаимодействие со мной" },
    { language: "id-ID", content: "Klik tombol\nMulai berinteraksi dengan saya" },
    { language: "pt-PT", content: "Toque no botão para interagir comigo!" },
    { language: "ms-MY", content: "Ketik butang untuk berinteraksi dengan saya!" },
    { language: "hi-IN", content: "बटन टैप करें और मुझसे बातचीत करें!" },
    { language: "ar", content: "اضغط الزر للتفاعل معي!" },
    { language: "it-IT", content: "Tocca pulsante per interagire con me!" },
    { language: "de-DE", content: "Tippen Sie die Taste, um mit mir zu interagieren!" },
];

export const INITIAL_GESTURE_HINTS: IApiInteractionData[] = [
    { language: "zh-TW", content: "對鏡頭比出手勢！開始跟我互動吧！" },
    { language: "taiwanese", content: "對鏡頭比出手勢！開始跟我互動吧！" },
    { language: "zh-CN", content: "对镜头比出手势！开始跟我互动吧！" },
    { language: "zh-HK", content: "對鏡頭比出手勢！開始跟我互動吧！" },
    { language: "yue", content: "對鏡頭比出手勢！開始同我互動啦！" },
    { language: "nan", content: "對鏡頭比出手勢！開始跟我互動吧！" },
    { language: "en-US", content: "Gesture to camera\nStart interacting with me" },
    { language: "en-GB", content: "Gesture to camera\nStart interacting with me" },
    { language: "ja-JP", content: "カメラに向かってジェスチャー\n私との交流を始めましょう" },
    { language: "ko-KR", content: "카메라를 향한 몸짓\n나와 교류를 시작해 보세요" },
    { language: "fr-FR", content: "Geste à la caméra\nCommencer à interagir avec moi" },
    { language: "es-ES", content: "Gesto de la cámara\nEmpieza conmigo" },
    { language: "ca-ES", content: "Gest de càmera\nComença per mi" },
    { language: "vi-VN", content: "Cử chỉ với máy ảnh\nBắt đầu tương tác với tôi" },
    { language: "th-TH", content: "ท่าทางไปที่กล้อง\nเริ่มโต้ตอบกับฉัน" },
    { language: "km-KH", content: "ធ្វើកាយវិការទៅកាមេរ៉ា\nចាប់ផ្តើមទំនាក់ទំនងជាមួយខ្ញុំ" },
    { language: "ro-RO", content: "Gest către cameră\nÎncepeți să interacționați cu mine" },
    { language: "ru-RU", content: "Жест в камеру\nНачать взаимодействие со мной" },
    { language: "id-ID", content: "Isyaratkan ke kamera\nMulai berinteraksi dengan saya" },
    { language: "pt-PT", content: "Faça um gesto para a câmara\npara interagir comigo!" },
    { language: "ms-MY", content: "Buat isyarat kepada kamera\nuntuk berinteraksi dengan saya!" },
    { language: "hi-IN", content: "कैमरे के सामने इशारा करें\nऔर मुझसे बातचीत करें!" },
    { language: "ar", content: "قم بالإيماءة أمام الكاميرا\nللتفاعل معي!" },
    { language: "it-IT", content: "Fai gesto davanti alla fotocamera\nper interagire con me!" },
    { language: "de-DE", content: "Gesten vor der Kamera ausführen,\num zu interagieren!" },
];

export const INITIAL_INTERACTION_SETTINGS: IApiInteraction = {
    enabled_interaction: false,
    interaction_mode: "touch",
    data: [],
};

// =============================================================================
// Layout Settings (Not implemented in components yet)
// =============================================================================

/**
 * 預設切版模式設定
 * Default: Standard layout mode
 * - ad_position: "right" for broadcast mode, "top" for section mode
 */
export const INITIAL_LAYOUT_SETTINGS: IApiLayoutMode = {
    mode: "standard",
    ad_position: "right",
};

// =============================================================================
// Subtitle Speed Settings (SubtitleSpeedSettings component)
// =============================================================================

/**
 * 預設字幕滾動速度設定
 * Default: zh-TW with speed 1.0
 */
export const INITIAL_SUBTITLE_SPEED: IApiSubtitleSpeed[] = [{ language: "zh-TW", speed: 1.0 }];

// =============================================================================
// Bidirectional Translation Settings (Not implemented in components yet)
// =============================================================================

/**
 * 預設雙向翻譯設定
 * Default: Disabled
 */
export const INITIAL_BIDIRECTIONAL_TRANSLATION_SETTINGS: IApiBidirectionalTranslation = {
    enabled: false,
};

// =============================================================================
// Games Toggle Settings (for kiosk_settings.native.games)
// =============================================================================

/**
 * 預設遊戲開關設定
 * Default: All games disabled
 */
export const INITIAL_GAMES_TOGGLE: IApiGamesToggle = {
    enabled: false,
    permission: {
        puzzle: false,
        quiz: false,
        "flip-card": false,
        prize: false,
    },
};

// =============================================================================
// Character Avatar Settings
// =============================================================================

/**
 * 預設角色上傳 API 資料
 */
export const INITIAL_CHARACTER_AVATAR: IApiCharacterAvatar = {
    mode: "3d",
    avatar_2d: {
        idle_video_url: "",
        talking_video_url: "",
        updated_at: "",
    },
    avatar_3d: {
        name: "",
        url: "",
        updated_at: "",
    },
};

/**
 * 預設角色上傳表單資料
 */
export const INITIAL_CHARACTER_AVATAR_FORM: ICharacterAvatarFormData = {
    mode: "3d",
    avatar_2d: {
        idle_video_url: "",
        talking_video_url: "",
        idleVideoFile: null,
        talkingVideoFile: null,
    },
    avatar_3d: {
        name: "",
        url: "",
        vrmFile: null,
    },
};
// Complete Kiosk Settings
// =============================================================================

/**
 * 完整預設 Kiosk Native 設定
 * 用於新角色建立時的初始設定
 * Aggregates all initial values into one complete settings object
 */
export const INITIAL_KIOSK_NATIVE_SETTINGS: IKioskSettingNative = {
    general: INITIAL_LANGUAGE_SETTINGS,
    schedule_marquee: INITIAL_MARQUEE_SETTINGS,
    ad: INITIAL_AD_SETTINGS,
    greetings: INITIAL_GREETINGS,
    hints: INITIAL_HINTS,
    QRcode: INITIAL_QRCODE_SETTINGS,
    festival_setting: INITIAL_FESTIVAL_SETTINGS,
    photo: INITIAL_PHOTO_SETTINGS,
    background: INITIAL_BACKGROUND_SETTINGS,
    volume: INITIAL_VOLUME_SETTINGS,
    weather: INITIAL_WEATHER_SETTINGS,
    time_alerts: INITIAL_TIME_ALERTS_SETTINGS,
    interaction: INITIAL_INTERACTION_SETTINGS,
    layout_mode: INITIAL_LAYOUT_SETTINGS,
    subtitle_speed: INITIAL_SUBTITLE_SPEED,
    bidirectional_translation: INITIAL_BIDIRECTIONAL_TRANSLATION_SETTINGS,
    games: INITIAL_GAMES_TOGGLE,
    character_avatar: INITIAL_CHARACTER_AVATAR,
};

// ================ Empty Langauge Content Factory ================
const createEmptyLangaugeContent = <T>(): IApiLanguageContent<T>[] => {
    return GAME_LANGUAGES.map(({ code }) => ({
        lang: code,
        content: [],
    }));
};

// ================ Item Factory ================
export const createPuzzleItem = (): IApiPuzzleItem => ({
    id: uuidv4(),
    image_url: "",
    title: "",
    description: "",
});
export const createQuizItem = (): IApiQuizItem => ({
    id: uuidv4(),
    title: "",
    image_url: "",
    options: [
        { text: "", image_url: "" },
        { text: "", image_url: "" },
        { text: "", image_url: "" },
        { text: "", image_url: "" },
    ],
    correct_option_index: 0,
});
export const createFlipCardItem = (): IApiFlipCardItem => ({
    id: uuidv4(),
    image_url: "",
    title: "",
    description: "",
});

export const createPrizeItem = (): IApiPrizeItem => ({
    id: uuidv4(),
    title: "",
    type: "none",
    url: "",
});

// ================ Initial Form Data ================
export const INITIAL_GAME_FORM_DATA: IGameSettingsFormData = {
    enabled: false,
    games: {
        puzzle: {
            enabled: false,
            game_time: GAME_TIME_RANGE.default,
            puzzle_count: PUZZLE_COUNT_RANGE.default,
            bgm: "",
            bg: "",
            bgmFile: null,
            bgFile: null,
            data: createEmptyLangaugeContent<IApiPuzzleItem>(),
        },
        quiz: {
            enabled: false,
            total_per_round: QUIZ_TOTAL_RANGE.default,
            pass_count: QUIZ_PASS_RANGE.default,
            bgm: "",
            bg: "",
            bgmFile: null,
            bgFile: null,
            data: createEmptyLangaugeContent<IApiQuizItem>(),
        },
        flipcard: {
            enabled: false,
            game_time: GAME_TIME_RANGE.default,
            pair_count: PAIR_COUNT_RANGE.default,
            bgm: "",
            bg: "",
            bgmFile: null,
            bgFile: null,
            data: createEmptyLangaugeContent<IApiFlipCardItem>(),
        },
    },
    prize: {
        common: {
            enabled: false,
            mode: "wheel",
            auto_start_countdown: AUTO_START_COUNTDOWN_RANGE.default,
        },
        data: createEmptyLangaugeContent<IApiPrizeItem>(),
    },
};

// =============== Helper: Get content for specific language ================
export const getContentByLangauge = <T>(data: IApiLanguageContent<T>[], lang: GameLanguageCode): T[] => {
    return data.find((item) => item.lang === lang)?.content ?? [];
};

// =============== Helper: Get game items for specific language ================
export const setContentByLangauge = <T>(
    data: IApiLanguageContent<T>[],
    lang: GameLanguageCode,
    content: T[]
): IApiLanguageContent<T>[] => {
    return data.find((item) => item.lang === lang) ? data : [...data, { lang, content }];
};
